import React from 'react';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';

import { useDeleteCodePromoMutation, useFetchCodePromosQuery, usePatchCodePromoMutation } from '@/api/hooks/useCodePromo';
import { useFetchRestaurantQuery } from '@/api/hooks/useRestaurant';
import { CodePromoResponse } from '@/api/lib/codePromo';
import { useAuthContext } from '@/contexts/auth';

import { Typography } from '@/components/atoms/Typography';
import { HeaderCouponDynamicBlock } from '@/components/molecules/HeaderCouponDynamicBlock';
import { CodePromotionButtons } from '@/components/organisms/CodePromotionButtons';
import { CodePromotionCard } from '@/components/organisms/CodePromotionCard';
import { TabPageLayout } from '@/components/templates/TabPageLayout';

export const CodePromoPage = () => {
  const { id } = useParams();
  const restaurantId = id || '';
  const { user } = useAuthContext();

  const { data: restaurant } = useFetchRestaurantQuery({ id: restaurantId, withMenu: false }, { enabled: !!restaurantId });
  const { data: coupons } = useFetchCodePromosQuery(restaurantId);
  const { mutate: deleteCoupon, isLoading: loadingDelete } = useDeleteCodePromoMutation({
    onSuccess: () => {
      toast.success('Coupon supprimé !', {
        position: 'bottom-right',
        duration: 5000
      });
    }
  });

  const { mutate: patchCoupon, isLoading: loadingPatch } = usePatchCodePromoMutation({
    onSuccess: () => {
      toast.success('Coupon modifié !', {
        position: 'bottom-right',
        duration: 5000
      });
    }
  });

  const handleDelete = async (couponId: string) => {
    deleteCoupon(couponId);
  };

  const handleActiveToggle = async (coupon: CodePromoResponse) => {
    patchCoupon({ couponId: coupon.id, codePromo: { active: !coupon.active }, restaurantId });
  };

  return (
    <TabPageLayout firstTitle="Code" secondTitle="Promotionnel" restaurantId={restaurantId}>
      <section className="border border-gray-300 gap-5 overflow-auto rounded-xl">
        <HeaderCouponDynamicBlock restaurant={restaurant} couponsLength={coupons?.length || 0} />
      </section>

      <section id="coupons" className="border border-gray-300 gap-5 p-4 md:p-10 rounded-xl">
        <Typography variant="h1" weight="semibold" marginClassName="mb-1">
          Vos coupons promotionnels
        </Typography>
        <Typography variant="paragraph" marginClassName="mb-5">
          Vous pouvez voir vos coupons promotionnels actifs et inactifs ci-dessous. Vous y retrouverez les actions possibles en dessous des coupons.
        </Typography>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 items-end">
          {coupons && coupons?.length >= 1 ? (
            coupons
              .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
              .map((coupon, index) => (
                <div key={coupon.id + index} className="space-y-2">
                  <CodePromotionCard
                    numberOfClick={0}
                    startDate={coupon.startDate}
                    endDate={coupon.endDate}
                    codePromo={coupon.codePromo}
                    offerName={coupon.offerName}
                    active={coupon.active}
                    isSupport={user?.isSupport}
                  />
                  <CodePromotionButtons
                    onDeleteClick={() => handleDelete(coupon.id)}
                    onToggleClick={() => handleActiveToggle(coupon)}
                    active={coupon.active}
                    isLoading={loadingDelete || loadingPatch}
                  />
                </div>
              ))
          ) : (
            <Typography color="lightGray">Aucun coupon promotionnel actif pour le moment.</Typography>
          )}
        </div>
      </section>
    </TabPageLayout>
  );
};
